/**
 * IndicatorCard — 通用健康指标卡片
 *
 * 用于：
 *   - 照见首页「关注的指标」网格
 *   - 维度矩阵指标网格
 */

import styles from './IndicatorCard.module.css';

export type IndicatorStatus = 'normal' | 'high' | 'low';

export interface IndicatorCardProps {
  name: string;
  value?: string | number | null;  // undefined/null = no recorded value
  unit: string;
  status?: IndicatorStatus;        // undefined = 不显示状态点
  refRange?: string;   // 参考范围字符串，如 "0.56-1.7"
  date?: string;       // ISO 日期，显示为 "3月10日"
  trend?: number;      // -1~1，有值时在底行右侧显示；undefined=不显示
  onClick?: () => void;
}

function formatDate(iso?: string): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (isNaN(d.getTime())) return null;
  return `${d.getMonth() + 1}月${d.getDate()}日`;
}

function TrendChip({ trend }: { trend: number }) {
  const hasTrend = Math.abs(trend) >= 0.001;
  if (!hasTrend) return null;
  const isUp = trend > 0;
  return (
    <span className={`${styles.trend} ${isUp ? styles.trendUp : styles.trendDown}`}>
      {isUp ? '↑' : '↓'} {Math.abs(trend * 100).toFixed(1)}%
    </span>
  );
}

const DOT_CLASS: Record<IndicatorStatus, string> = {
  normal: styles.dotNormal,
  high:   styles.dotHigh,
  low:    styles.dotLow,
};

function StatusDot({ status }: { status?: IndicatorStatus }) {
  if (!status) return null;
  return <span className={`${styles.statusDot} ${DOT_CLASS[status]}`} />;
}

export function IndicatorCard({
  name, value, unit, status, refRange, date, trend, onClick,
}: IndicatorCardProps) {
  const dateStr = formatDate(date);
  const showFooter = refRange || dateStr || (trend !== undefined && Math.abs(trend) >= 0.001);

  return (
    <button className={styles.card} onClick={onClick}>
      {/* 顶行：名称 + 状态点 */}
      <div className={styles.header}>
        <span className={styles.name}>{name}</span>
        <StatusDot status={status} />
      </div>

      {/* 数值 */}
      <div className={styles.valueRow}>
        {value != null ? (
          <>
            <span className={styles.value}>{value}</span>
            <span className={styles.unit}>{unit}</span>
          </>
        ) : (
          <span className={styles.value} style={{ fontSize: 'var(--text-sm)', color: 'var(--color-ash)', fontFamily: 'var(--font-sans)' }}>
            未记录
          </span>
        )}
      </div>

      {/* 底行：参考范围 | 日期 [变化率] */}
      {showFooter && (
        <div className={styles.footer}>
          {refRange ? (
            <span className={styles.refRange}>
              <span className={styles.refRangeLabel}>参考</span>{refRange}
            </span>
          ) : (
            <span />
          )}
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
            {dateStr && <span className={styles.date}>{dateStr}</span>}
            {trend !== undefined && <TrendChip trend={trend} />}
          </div>
        </div>
      )}
    </button>
  );
}
